import Typesense from 'typesense'

// `exact: true` disables typo tolerance for identifier-style fields so searches
// like "50246" do not fuzzy-match "50146". Prefix matching stays enabled, so
// "50246" still matches tokens like "50246/1" and "50246/2".
const SEARCH_FIELD_SPECS = [
  { name: 'assessorName', exact: false },
  { name: 'quoteNo',      exact: true },
  { name: 'customer',     exact: false },
  { name: 'insurer',      exact: false },
  { name: 'makeModel',    exact: false },
  { name: 'regoNo',       exact: true },
  { name: 'claimNo',      exact: true },
  { name: 'colour',       exact: false },
  { name: 'phoneNo',      exact: true },
  { name: 'vinNumber',    exact: true },
  { name: 'labourType',   exact: false },
  { name: 'buildYear',    exact: true },
  { name: 'partNumbers',  exact: true },
  { name: 'pos.pcNo',     exact: true },
  { name: 'pos.no',       exact: true },
  { name: 'pos.vendor',   exact: false },
  { name: 'invoices.no',  exact: true }
]

const SEARCH_FIELDS = SEARCH_FIELD_SPECS.map(f => f.name).join(',')
const NUM_TYPOS = SEARCH_FIELD_SPECS.map(f => (f.exact ? 0 : 2)).join(',')

const MIN_SEARCH_LENGTH = 2
const PER_PAGE = 10
const CONNECTION_TIMEOUT_SECONDS = 2
const HIGHLIGHT_START_TAG = '<em>'
const HIGHLIGHT_END_TAG = '</em>'

class TypesenseSearchService {
  constructor() {
    this.client = null
    this.collectionName = process.env.VUE_APP_GLOBAL_SEARCH_COLLECTION
  }

  // Initialize the client with a scoped API key
  initialize(scopedApiKey) {
    if (!scopedApiKey) {
      console.error('Scoped API key is required to initialize Typesense client')
      return
    }

    this.client = new Typesense.Client({
      nodes: [{
        host: process.env.VUE_APP_GLOBAL_SEARCH_HOST,
        port: 443,                                   //TODO:  do we need env vars here?
        protocol: 'https'
      }],
      // read onlt scoped (companyid) key
      apiKey: scopedApiKey,
      connectionTimeoutSeconds: CONNECTION_TIMEOUT_SECONDS
    })
  }

  // `signal` (an AbortSignal) cancels the request, including any retries.
  async performSearch(searchString = '', { signal = null } = {}) {
    if (!this.client) {
      console.error('Typesense client is not initialized. Call initialize(scopedApiKey) first.')
      return {
        result: {
          result: {
            hits: []
          }
        }
      }
    }

    const trimmedSearchString = searchString.trim()
    if (!trimmedSearchString || trimmedSearchString.length < MIN_SEARCH_LENGTH) {
      return {
        result: {
          result: {
            hits: []
          }
        }
      }
    }

    try {
      const searchParameters = {
        q: trimmedSearchString,
        query_by: SEARCH_FIELDS,
        num_typos: NUM_TYPOS,
        highlight_full_fields: SEARCH_FIELDS,
        highlight_start_tag: HIGHLIGHT_START_TAG,
        highlight_end_tag: HIGHLIGHT_END_TAG,
        per_page: PER_PAGE
      }

      const searchResults = await this.client
        .collections(this.collectionName)
        .documents()
        .search(searchParameters, { abortSignal: signal })

      const hits = searchResults.hits.map(hit => ({
        ...hit.document,
        highlight: hit.highlight || {}
      }))

      return {
        result: {
          result: {
            hits: hits
          }
        }
      }
    } catch (error) {
      // A cancelled search is expected (a newer one replaced it), not an error.
      if (!(signal && signal.aborted)) {
        console.error('Typesense search error:', error)
      }
      throw error
    }
  }
  async checkGlobalSearchEnabled() {
    if (!this.client) {
      console.error('Typesense client is not initialized. Call initialize(scopedApiKey) first.')
      return false
    }

    try {
      // Use direct Typesense client to check if collection is accessible
      const searchParameters = {
        q: '*',
        query_by: 'quoteNo',
        per_page: 1
      }

      await this.client.collections(this.collectionName).documents().search(searchParameters)
      return true
    } catch (error) {
      console.error('Typesense collection check error:', error)
      return false
    }
  }
}

export default new TypesenseSearchService()
